// src/hooks/usePushNotifications.js
// Hook for managing browser push notification subscriptions

import { useState, useEffect, useCallback } from 'react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

// VAPID public key from environment
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Detect device/browser name
function getDeviceName() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return `${browser} on ${os}`;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check browser support
  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      VAPID_PUBLIC_KEY;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    } else {
      setLoading(false);
    }
  }, []);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!isSupported || !user?.id) {
      setLoading(false);
      return;
    }

    const initializeServiceWorker = async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        console.log('[Push] Service Worker registered');

        // Wait for ready
        await navigator.serviceWorker.ready;

        // Check existing subscription
        const existingSub = await registration.pushManager.getSubscription();

        if (existingSub) {
          console.log('[Push] Existing subscription found');
          setSubscription(existingSub);
          setIsSubscribed(true);

          // Verify it exists in database
          const { data: dbSub } = await supabase
            .from('push_subscriptions')
            .select('id, is_active')
            .eq('user_id', user.id)
            .eq('endpoint', existingSub.endpoint)
            .maybeSingle();

          if (!dbSub || !dbSub.is_active) {
            // Re-save to database
            await saveSubscriptionToDatabase(existingSub);
          }
        } else {
          setIsSubscribed(false);
        }
      } catch (err) {
        console.error('[Push] Service Worker registration failed:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeServiceWorker();
  }, [isSupported, user?.id]);

  // Listen for notification clicks from service worker
  useEffect(() => {
    if (!isSupported) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        window.location.href = event.data.url;
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [isSupported]);

  // Save subscription to database
  const saveSubscriptionToDatabase = async (sub) => {
    if (!user?.id || !user?.tenant_id) return;

    const subscriptionJSON = sub.toJSON();

    const { error: dbError } = await supabase
      .from('push_subscriptions')
      .upsert(
        {
          user_id: user.id,
          tenant_id: user.tenant_id,
          endpoint: subscriptionJSON.endpoint,
          p256dh: subscriptionJSON.keys.p256dh,
          auth: subscriptionJSON.keys.auth,
          device_name: getDeviceName(),
          user_agent: navigator.userAgent,
          is_active: true,
          updated_at: new Date().toISOString()
        },
        {
          onConflict: 'user_id,endpoint'
        }
      );

    if (dbError) {
      console.error('[Push] Failed to save subscription:', dbError);
      throw dbError;
    }

    console.log('[Push] Subscription saved to database');
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !user?.id) {
      throw new Error('Push notifications not supported or user not logged in');
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      console.log('[Push] New subscription created');

      // Save to database
      await saveSubscriptionToDatabase(pushSubscription);

      setSubscription(pushSubscription);
      setIsSubscribed(true);

      return pushSubscription;
    } catch (err) {
      console.error('[Push] Subscription failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isSupported, user?.id, user?.tenant_id]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || !user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from database
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id)
        .eq('endpoint', subscription.endpoint);

      setSubscription(null);
      setIsSubscribed(false);

      console.log('[Push] Unsubscribed successfully');
    } catch (err) {
      console.error('[Push] Unsubscribe failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [subscription, user?.id]);

  // Get all user's subscriptions
  const getSubscriptions = useCallback(async () => {
    if (!user?.id) return [];

    const { data, error: fetchError } = await supabase
      .from('push_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (fetchError) {
      console.error('[Push] Failed to fetch subscriptions:', fetchError);
      return [];
    }

    return data || [];
  }, [user?.id]);

  // Remove specific subscription by ID
  const removeSubscription = useCallback(
    async (subscriptionId) => {
      if (!user?.id) return;

      const { error: deleteError } = await supabase
        .from('push_subscriptions')
        .delete()
        .eq('id', subscriptionId)
        .eq('user_id', user.id);

      if (deleteError) {
        throw deleteError;
      }
    },
    [user?.id]
  );

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    getSubscriptions,
    removeSubscription
  };
}

export default usePushNotifications;
